import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import {
  fetchAvailableGateways,
  addGatewayToMonitoring,
} from "../../store/gatewaysSlice";
import { selectCurrentMap } from "../../store/mapsSlice";
import { IGateway } from "../../types";

interface GatewayPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGatewayAdded?: (gateway: IGateway) => void;
}

const GatewayPickerModal: React.FC<GatewayPickerModalProps> = ({
  isOpen,
  onClose,
  onGatewayAdded,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { language } = useSelector((state: RootState) => state.ui);
  const currentMap = useSelector(selectCurrentMap);
  const { available, loading, error } = useSelector(
    (state: RootState) => state.gateways
  );

  const [selectedGateway, setSelectedGateway] = useState<string | null>(null);
  const [lastSeenFilter, setLastSeenFilter] = useState<number | null>(3);
  const [gatewaySettings, setGatewaySettings] = useState({
    position: { x: 100, y: 100 },
    radius: 75,
    displayName: "",
    color: "#10B981",
    showCoverage: true,
    logicThreshold: -60,
    maxRSSI: -30,
  });
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualGateway, setManualGateway] = useState({
    mac: "",
    name: "",
  });

  // Track last fetch key to prevent refetch loops (persist across dev remounts)
  const lastFetchedKey = useRef<string | null>(
    (window as any).__gatewayPickerLastKey ?? null
  );

  // Fetch available gateways once per filter value while open
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const key = `${lastSeenFilter ?? "all"}`;
    if (lastFetchedKey.current === key) {
      return; // already fetched for this filter
    }

    lastFetchedKey.current = key;
    (window as any).__gatewayPickerLastKey = key;
    dispatch(fetchAvailableGateways(lastSeenFilter || null));
  }, [dispatch, isOpen, lastSeenFilter]);

  const handleSelectGateway = (gateway: IGateway) => {
    if (selectedGateway === gateway.mac) {
      setSelectedGateway(null);
      resetSettings();
    } else {
      setSelectedGateway(gateway.mac);
      setGatewaySettings({
        position: { x: 100, y: 100 },
        radius: 75,
        displayName: gateway.name || gateway.mac,
        color: "#10B981",
        showCoverage: true,
        logicThreshold: -60,
        maxRSSI: -30,
      });
    }
  };

  const resetSettings = () => {
    setGatewaySettings({
      position: { x: 100, y: 100 },
      radius: 75,
      displayName: "",
      color: "#10B981",
      showCoverage: true,
      logicThreshold: -60,
      maxRSSI: -30,
    });
  };

  const handleAddToMonitoring = async (gateway: IGateway) => {
    if (!currentMap) {
      console.error("No map selected");
      alert(
        language === "he" ? "יש לבחור מפה תחילה" : "Please select a map first"
      );
      return;
    }

    try {
      const result = await dispatch(
        addGatewayToMonitoring({
          mac: gateway.mac,
          position: gatewaySettings.position,
          radius: gatewaySettings.radius,
          mapId: currentMap._id!,
          settings: {
            displayName: gatewaySettings.displayName,
            color: gatewaySettings.color,
            showCoverage: gatewaySettings.showCoverage,
            logicThreshold: gatewaySettings.logicThreshold,
            maxRSSI: gatewaySettings.maxRSSI,
          },
        })
      );

      if (result.meta.requestStatus === "fulfilled") {
        onGatewayAdded?.(gateway);
        onClose();
        resetState();
      }
    } catch (error) {
      console.error("Failed to add gateway to monitoring:", error);
    }
  };

  const handleManualAdd = async () => {
    if (!currentMap) {
      console.error("No map selected");
      alert(
        language === "he" ? "יש לבחור מפה תחילה" : "Please select a map first"
      );
      return;
    }

    if (manualGateway.mac && manualGateway.name) {
      const gateway: IGateway = {
        mac: manualGateway.mac.toUpperCase(),
        name: manualGateway.name,
        position: gatewaySettings.position,
        radius: gatewaySettings.radius,
        isMonitored: false,
        isAvailableInAPI: false,
      };

      try {
        const result = await dispatch(
          addGatewayToMonitoring({
            mac: gateway.mac,
            position: gatewaySettings.position,
            radius: gatewaySettings.radius,
            mapId: currentMap._id!,
            settings: {
              displayName: manualGateway.name,
              color: gatewaySettings.color,
              showCoverage: gatewaySettings.showCoverage,
              logicThreshold: gatewaySettings.logicThreshold,
              maxRSSI: gatewaySettings.maxRSSI,
            },
          })
        );

        if (result.meta.requestStatus === "fulfilled") {
          onGatewayAdded?.(gateway);
          onClose();
          resetState();
        }
      } catch (error) {
        console.error("Failed to add manual gateway to monitoring:", error);
      }
    }
  };

  const resetState = () => {
    setSelectedGateway(null);
    setShowManualEntry(false);
    setManualGateway({ mac: "", name: "" });
    resetSettings();
  };

  const formatLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return language === "he" ? "לא זמין" : "N/A";
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMinutes = (now.getTime() - date.getTime()) / (1000 * 60);

    if (diffMinutes < 1) {
      return language === "he" ? "זה עתה" : "Just now";
    } else if (diffMinutes < 60) {
      return language === "he"
        ? `לפני ${Math.floor(diffMinutes)} דקות`
        : `${Math.floor(diffMinutes)} min ago`;
    } else if (diffMinutes < 24 * 60) {
      return language === "he"
        ? `לפני ${Math.floor(diffMinutes / 60)} שעות`
        : `${Math.floor(diffMinutes / 60)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (!isOpen) return null;

  const portalTarget = document.body || document.getElementById("root")!;
  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2147483647] p-4 pointer-events-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {language === "he"
                ? "הוסף שער למעקב"
                : "Add Gateway to Monitoring"}
            </h2>
            <button
              onClick={() => {
                onClose();
                resetState();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Filter and Toggle Controls */}
          <div className="mt-4 space-y-3">
            {/* Time Filter */}
            {!showManualEntry && (
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <label className="text-sm font-medium text-gray-700">
                  {language === "he" ? "סנן לפי ימים:" : "Filter by days:"}
                </label>
                <select
                  value={lastSeenFilter || ""}
                  onChange={(e) =>
                    setLastSeenFilter(
                      e.target.value ? parseInt(e.target.value) : null
                    )
                  }
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">{language === "he" ? "הכל" : "All"}</option>
                  <option value="3">
                    {language === "he" ? "3 ימים אחרונים" : "Last 3 days"}
                  </option>
                  <option value="10">
                    {language === "he" ? "10 ימים אחרונים" : "Last 10 days"}
                  </option>
                  <option value="50">
                    {language === "he" ? "50 ימים אחרונים" : "Last 50 days"}
                  </option>
                  <option value="100">
                    {language === "he" ? "100 ימים אחרונים" : "Last 100 days"}
                  </option>
                </select>
              </div>
            )}

            {/* Toggle between Available and Manual */}
            {/* <div className="flex space-x-2 rtl:space-x-reverse">
              <button
                onClick={() => setShowManualEntry(false)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  !showManualEntry
                    ? "bg-green-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {language === "he" ? "מ-API" : "From API"}
              </button>
              <button
                onClick={() => setShowManualEntry(true)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  showManualEntry
                    ? "bg-green-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {language === "he" ? "הוספה ידנית" : "Manual Entry"}
              </button>
            </div> */}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden overflow-y-scroll flex">
          {/* Left Panel - Device/Gateway List */}
          <div className="w-1/2 flex flex-col border-r border-gray-200">
            {showManualEntry ? (
              /* Manual Entry Form */
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "כתובת MAC" : "MAC Address"}
                  </label>
                  <input
                    type="text"
                    value={manualGateway.mac}
                    onChange={(e) =>
                      setManualGateway({
                        ...manualGateway,
                        mac: e.target.value,
                      })
                    }
                    placeholder="XX:XX:XX:XX:XX:XX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "שם שער" : "Gateway Name"}
                  </label>
                  <input
                    type="text"
                    value={manualGateway.name}
                    onChange={(e) =>
                      setManualGateway({
                        ...manualGateway,
                        name: e.target.value,
                      })
                    }
                    placeholder={
                      language === "he" ? "הכנס שם שער" : "Enter gateway name"
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
            ) : (
              /* Available Gateways List */
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-6 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">
                      {language === "he" ? "טוען..." : "Loading..."}
                    </p>
                  </div>
                ) : error ? (
                  <div className="p-6 text-center text-red-600">
                    <p>{error}</p>
                  </div>
                ) : available.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
                      />
                    </svg>
                    <p className="mt-2">
                      {language === "he"
                        ? "אין שערים זמינים"
                        : "No gateways available"}
                    </p>
                    <p className="text-sm mt-1">
                      {language === "he"
                        ? "שערים יופיעו כאן לאחר סנכרון עם ה-API"
                        : "Gateways will appear here after syncing with the API"}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {available.map((gateway) => {
                      const isSelected = selectedGateway === gateway.mac;
                      return (
                        <div
                          key={gateway.mac}
                          className={`p-4 cursor-pointer transition-colors duration-200 ${
                            isSelected
                              ? "bg-green-50 border-l-4 border-green-500"
                              : "hover:bg-gray-50"
                          }`}
                          onClick={() => handleSelectGateway(gateway)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">
                                {gateway.name || gateway.mac}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {gateway.mac}
                              </p>
                              <div className="flex items-center space-x-4 rtl:space-x-reverse mt-1">
                                <span className="text-xs text-green-600 font-medium">
                                  {language === "he"
                                    ? "זמין ב-API"
                                    : "Available in API"}
                                </span>
                                {gateway.lastSeen && (
                                  <span className="text-xs text-gray-500">
                                    {language === "he"
                                      ? "נראה לאחרונה:"
                                      : "Last seen:"}{" "}
                                    {formatLastSeen(gateway.lastSeen)}
                                  </span>
                                )}
                                {gateway.daysSinceLastSeen !== undefined && (
                                  <span className="text-xs text-blue-600">
                                    {gateway.daysSinceLastSeen === 0
                                      ? language === "he"
                                        ? "פעיל היום"
                                        : "Active today"
                                      : language === "he"
                                      ? `לפני ${gateway.daysSinceLastSeen} ימים`
                                      : `${gateway.daysSinceLastSeen} days ago`}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center">
                              {isSelected ? (
                                <svg
                                  className="w-5 h-5 text-green-600"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                </svg>
                              ) : (
                                <svg
                                  className="w-5 h-5 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Gateway Settings */}
          <div className="w-1/2 flex flex-col">
            <div className="p-6 bg-green-50 border-b border-green-200">
              <h3 className="font-medium text-gray-900 mb-4">
                {language === "he" ? "הגדרות שער" : "Gateway Settings"}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "שם תצוגה" : "Display Name"}
                  </label>
                  <input
                    type="text"
                    value={gatewaySettings.displayName}
                    onChange={(e) =>
                      setGatewaySettings({
                        ...gatewaySettings,
                        displayName: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {language === "he" ? "מיקום X" : "Position X"}
                    </label>
                    <input
                      type="number"
                      value={gatewaySettings.position.x}
                      onChange={(e) =>
                        setGatewaySettings({
                          ...gatewaySettings,
                          position: {
                            ...gatewaySettings.position,
                            x: parseInt(e.target.value) || 0,
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {language === "he" ? "מיקום Y" : "Position Y"}
                    </label>
                    <input
                      type="number"
                      value={gatewaySettings.position.y}
                      onChange={(e) =>
                        setGatewaySettings({
                          ...gatewaySettings,
                          position: {
                            ...gatewaySettings.position,
                            y: parseInt(e.target.value) || 0,
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "רדיוס כיסוי" : "Coverage Radius"}
                  </label>
                  <input
                    type="number"
                    value={gatewaySettings.radius}
                    onChange={(e) =>
                      setGatewaySettings({
                        ...gatewaySettings,
                        radius: parseInt(e.target.value) || 0,
                      })
                    }
                    min="10"
                    max="200"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  />
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "he" ? "צבע" : "Color"}
                  </label>
                  <input
                    type="color"
                    value={gatewaySettings.color}
                    onChange={(e) =>
                      setGatewaySettings({
                        ...gatewaySettings,
                        color: e.target.value,
                      })
                    }
                    className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                  />
                </div> */}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {language === "he"
                        ? "סף RSSI לוגי"
                        : "Logic RSSI Threshold"}
                    </label>
                    <input
                      type="number"
                      value={gatewaySettings.logicThreshold}
                      onChange={(e) =>
                        setGatewaySettings({
                          ...gatewaySettings,
                          logicThreshold: parseInt(e.target.value) || -60,
                        })
                      }
                      min="-100"
                      max="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {language === "he" ? "RSSI מקסימלי" : "Max RSSI"}
                    </label>
                    <input
                      type="number"
                      value={gatewaySettings.maxRSSI}
                      onChange={(e) =>
                        setGatewaySettings({
                          ...gatewaySettings,
                          maxRSSI: parseInt(e.target.value) || -30,
                        })
                      }
                      min="-100"
                      max="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showCoverage"
                    checked={gatewaySettings.showCoverage}
                    onChange={(e) =>
                      setGatewaySettings({
                        ...gatewaySettings,
                        showCoverage: e.target.checked,
                      })
                    }
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <label
                    htmlFor="showCoverage"
                    className="ml-2 text-sm text-gray-700"
                  >
                    {language === "he" ? "הצג כיסוי" : "Show Coverage"}
                  </label>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 mt-auto">
              <div className="flex justify-end space-x-3 rtl:space-x-reverse">
                <button
                  onClick={() => {
                    onClose();
                    resetState();
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
                >
                  {language === "he" ? "ביטול" : "Cancel"}
                </button>
                <button
                  onClick={() => {
                    if (showManualEntry) {
                      handleManualAdd();
                    } else {
                      const gateway = available.find(
                        (g) => g.mac === selectedGateway
                      );
                      if (gateway) handleAddToMonitoring(gateway);
                    }
                  }}
                  disabled={
                    showManualEntry
                      ? !manualGateway.mac || !manualGateway.name
                      : !selectedGateway
                  }
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-md transition-colors"
                >
                  {language === "he" ? "הוסף למעקב" : "Add to Monitoring"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    portalTarget
  );
};

export default GatewayPickerModal;
